// ============================================================================
// Client-detail pure helpers — no React, no side effects.
// ----------------------------------------------------------------------------
// Mirrors the backend engine semantics (viccofitness/metrics.py, goals.py,
// client_metrics.py) for the bits the UI needs to compute or label client-side:
//   • age from an ISO birthday
//   • BMI / WHR / WHtR category labels + chart threshold reference lines
//   • the per-goal progress + linear projection (least-squares slope -> ETA)
//   • the structured WEEKLY AVAILABILITY codec (a JSON convention stored in the
//     free-text `client.availability` column, with graceful fallback so legacy
//     plain-text availability still renders).
// All "optional-first": a missing input yields null / an empty result, never a
// throw and never a fake zero.
// ============================================================================
import type { ApiResult, Goal, SeriesPoint } from "../../api";
import { intlLocale } from "../../i18n/config";

// --- age --------------------------------------------------------------------

/** Whole years between an ISO birthday and today (null if missing/malformed). */
export function ageFromBirthday(
  birthday: string | null | undefined,
  today: Date = new Date(),
): number | null {
  if (!birthday) return null;
  const b = new Date(birthday);
  if (Number.isNaN(b.getTime())) return null;
  let age = today.getFullYear() - b.getFullYear();
  const m = today.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < b.getDate())) age -= 1;
  return age >= 0 ? age : null;
}

// --- sex --------------------------------------------------------------------

export type SexCode = "m" | "f" | "";

/** Normalize the loose backend sex string to one of our three codes. */
export function normalizeSex(sex: string | null | undefined): SexCode {
  const s = (sex ?? "").trim().toLowerCase();
  if (s === "m" || s === "male") return "m";
  if (s === "f" || s === "female") return "f";
  return "";
}

// --- metric labelling (mirrors metrics.py note semantics) -------------------

/** WHO BMI category key (standard cut-points). Matches metrics.bmi_category. */
export function bmiCategory(bmi: number): string {
  if (bmi < 18.5) return "underweight";
  if (bmi < 25) return "normal";
  if (bmi < 30) return "overweight";
  if (bmi < 35) return "obese_i";
  if (bmi < 40) return "obese_ii";
  return "obese_iii";
}

export type RiskTone = "success" | "warning" | "danger" | "neutral";

/** Badge tone for a BMI category. */
export function bmiTone(category: string): RiskTone {
  if (category === "normal") return "success";
  if (category === "underweight" || category === "overweight") return "warning";
  return "danger";
}

/** WHtR band (matches metrics.whtr note: >=0.6 high, >=0.5 increased). */
export function whtrBand(v: number): { key: string; tone: RiskTone } {
  if (v >= 0.6) return { key: "high", tone: "danger" };
  if (v >= 0.5) return { key: "increased", tone: "warning" };
  return { key: "healthy", tone: "success" };
}

/** WHR risk band by sex (matches metrics.whr: m>=0.90, f>=0.85 elevated). */
export function whrBand(
  v: number,
  sex: SexCode,
): { key: string; tone: RiskTone } {
  const threshold = sex === "f" ? 0.85 : 0.9;
  if (sex === "") {
    // No sex on file → can't band by sex; use the stricter female threshold.
    return v >= 0.85
      ? { key: "elevated_risk", tone: "warning" }
      : { key: "normal", tone: "neutral" };
  }
  return v >= threshold
    ? { key: "elevated_risk", tone: "danger" }
    : { key: "normal", tone: "success" };
}

/** The healthy-ceiling reference line value for a ratio metric, for charts. */
export function ratioThreshold(metric: "whr" | "whtr", sex: SexCode): number {
  if (metric === "whtr") return 0.5;
  return sex === "f" ? 0.85 : 0.9;
}

// --- number / date formatting ----------------------------------------------

export function fmtNum(n: number, decimals = 1): string {
  return n.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/** A short, locale-aware date label, e.g. "16 Jun 2026". null-safe. */
export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(intlLocale(), {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** "16 Jun, 14:30" — date + time for scheduled sessions. */
export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(intlLocale(), {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

// --- result unwrap ----------------------------------------------------------

/** Pull a numeric value from an engine Result, or null when it didn't compute. */
export function resultValue(r: ApiResult | undefined): number | null {
  if (!r || !r.ok || r.value === null) return null;
  return r.value;
}

// ============================================================================
// WEEKLY AVAILABILITY codec
// ----------------------------------------------------------------------------
// The backend stores availability as a single free-text column. We define a
// compact JSON convention so the structured picker round-trips, and we fall
// back to legacy free text (rendered verbatim) when the value isn't our JSON.
// ============================================================================

export const DAY_KEYS = [
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
  "sun",
] as const;
export type DayKey = (typeof DAY_KEYS)[number];

export interface DayAvailability {
  on: boolean;
  from: string; // "HH:MM"
  to: string; // "HH:MM"
}

export type WeeklyAvailability = Record<DayKey, DayAvailability>;

const MAGIC = "vfwk1"; // version tag so we can evolve the format later.

export function emptyAvailability(): WeeklyAvailability {
  return DAY_KEYS.reduce((acc, d) => {
    acc[d] = { on: false, from: "09:00", to: "17:00" };
    return acc;
  }, {} as WeeklyAvailability);
}

interface ParsedAvailability {
  weekly: WeeklyAvailability;
  /** Non-null when the stored value was legacy free text (not our JSON). */
  legacyText: string | null;
}

function isHHMM(s: unknown): s is string {
  return typeof s === "string" && /^\d{2}:\d{2}$/.test(s);
}

/** Parse the stored availability string into a structured week (+legacy text). */
export function parseAvailability(
  raw: string | null | undefined,
): ParsedAvailability {
  const weekly = emptyAvailability();
  if (!raw || !raw.trim()) return { weekly, legacyText: null };

  try {
    const obj = JSON.parse(raw) as unknown;
    if (
      obj &&
      typeof obj === "object" &&
      (obj as { v?: string }).v === MAGIC &&
      (obj as { days?: unknown }).days &&
      typeof (obj as { days: unknown }).days === "object"
    ) {
      const days = (obj as { days: Record<string, unknown> }).days;
      for (const d of DAY_KEYS) {
        const entry = days[d];
        if (entry && typeof entry === "object") {
          const e = entry as Partial<DayAvailability>;
          weekly[d] = {
            on: Boolean(e.on),
            from: isHHMM(e.from) ? e.from : "09:00",
            to: isHHMM(e.to) ? e.to : "17:00",
          };
        }
      }
      return { weekly, legacyText: null };
    }
  } catch {
    // not our JSON — treat as legacy free text below
  }
  return { weekly, legacyText: raw.trim() };
}

/** Serialize a structured week back to the stored string (null if all off). */
export function serializeAvailability(weekly: WeeklyAvailability): string | null {
  const anyOn = DAY_KEYS.some((d) => weekly[d].on);
  if (!anyOn) return "";
  return JSON.stringify({ v: MAGIC, days: weekly });
}

/** True if at least one day is marked available. */
export function hasAnyAvailability(weekly: WeeklyAvailability): boolean {
  return DAY_KEYS.some((d) => weekly[d].on);
}

// ============================================================================
// GOAL progress + projection
// ----------------------------------------------------------------------------
// Pure port of goals.py for the values the UI shows live: current value (from
// the matching measurement series or latest weight), percent progress, and a
// linear-fit projected ETA date. Optional-first: anything not computable comes
// back null so the UI can omit it rather than fake it.
// ============================================================================

export type GoalDirection = "reduce" | "gain";

export type GoalSeriesKey = "weight" | "body_fat" | "bmi" | "whr" | "whtr";

/**
 * Map a goal's metric_key to the measurement series that tracks it. Weight is
 * the common case; ratios + body-fat map to their computed series. Unknown
 * keys (or weight) fall back to the weight series.
 */
export function goalSeriesKey(
  metricKey: string | null | undefined,
): GoalSeriesKey {
  const k = (metricKey ?? "").trim().toLowerCase();
  if (k.includes("body") || k.includes("fat") || k === "bf") return "body_fat";
  if (k === "bmi") return "bmi";
  if (k === "whr") return "whr";
  if (k === "whtr" || k === "wht") return "whtr";
  return "weight";
}

/** Infer a sensible direction when the goal didn't store one. */
export function inferDirection(goal: Goal): GoalDirection {
  if (goal.direction === "reduce" || goal.direction === "gain") {
    return goal.direction;
  }
  if (goal.baseline_value != null && goal.target_value != null) {
    return goal.target_value < goal.baseline_value ? "reduce" : "gain";
  }
  return "reduce";
}

/** progress_pct port: percent from baseline toward target (null if undefined). */
export function progressPct(
  baseline: number | null,
  current: number | null,
  target: number | null,
  direction: GoalDirection,
): number | null {
  if (baseline == null || current == null || target == null) return null;
  if (baseline === target) return null;
  const pct =
    direction === "reduce"
      ? ((baseline - current) / (baseline - target)) * 100
      : ((current - baseline) / (target - baseline)) * 100;
  return pct;
}

/** weekly_rate port: least-squares slope of value/week over a series. */
export function weeklyRate(series: SeriesPoint[]): number | null {
  const pts: Array<[number, number]> = [];
  for (const p of series) {
    const d = new Date(p.x);
    if (Number.isNaN(d.getTime()) || p.y == null) continue;
    pts.push([d.getTime() / 86_400_000, p.y]); // x in days
  }
  if (pts.length < 2) return null;
  const base = Math.min(...pts.map(([x]) => x));
  const xs = pts.map(([x]) => x - base);
  const ys = pts.map(([, y]) => y);
  if (new Set(xs).size < 2) return null;
  const n = xs.length;
  const mx = xs.reduce((a, b) => a + b, 0) / n;
  const my = ys.reduce((a, b) => a + b, 0) / n;
  const denom = xs.reduce((a, x) => a + (x - mx) ** 2, 0);
  if (denom === 0) return null;
  const slopePerDay =
    xs.reduce((a, x, i) => a + (x - mx) * (ys[i] - my), 0) / denom;
  return slopePerDay * 7;
}

export interface Projection {
  /** Projected ISO date the current value reaches target. */
  etaIso: string;
  /** Weeks from now until target on the current trend. */
  weeks: number;
  ratePerWeek: number;
}

/** project_date port: ETA on the linear trend (null when unreachable/flat). */
export function projectDate(
  current: number | null,
  target: number | null,
  ratePerWeek: number | null,
  now: Date = new Date(),
): Projection | null {
  if (current == null || target == null || ratePerWeek == null) return null;
  if (current === target) {
    return { etaIso: now.toISOString().slice(0, 10), weeks: 0, ratePerWeek };
  }
  if (Math.abs(ratePerWeek) < 1e-9) return null;
  const weeks = (target - current) / ratePerWeek;
  if (weeks <= 0) return null; // rate points away from target
  const eta = new Date(now.getTime() + weeks * 7 * 86_400_000);
  return { etaIso: eta.toISOString().slice(0, 10), weeks, ratePerWeek };
}
